import { FiltersType } from '@/api/types';
import { client } from '@/client';
import {
  DeleteEnrollmentPayload,
  ForceCompleteRequest,
  ListCourseUsersResponse,
} from '@/client/courses';
import { MessageResponseModel } from '@/client/models';
import {
  DeleteScheduleMutation,
  ForceCompleteCourseMutation,
  GenericForbiddenApiError,
} from '@/hooks/query';
import { COURSES_QUERY_KEY } from '@/hooks/query/courses.query-keys';
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

export const useDeleteEnrollment = (): {
  post: (payload: DeleteEnrollmentPayload) => Promise<MessageResponseModel>;
} & DeleteScheduleMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    DeleteEnrollmentPayload
  >('deleteEnrollment', (payload: DeleteEnrollmentPayload) =>
    client.courses.deleteEnrollment(payload),
  );

  return { post: mutateAsync, ...(rest as any) };
};

export const useForceComplete = (): {
  complete: (payload: ForceCompleteRequest) => Promise<MessageResponseModel>;
} & ForceCompleteCourseMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    ForceCompleteRequest
  >('forceCompleteCourse', (payload: ForceCompleteRequest) =>
    client.courses.forceComplete(payload),
  );

  return { complete: mutateAsync, ...(rest as any) };
};

export const useCourseUsers = (
  params: UseQueryOptions & {
    take?: number;
    skip?: number;
    sort?: string[];
    filters?: FiltersType;
  },
  courseId?: string,
  accountId?: string,
): UseQueryResult<ListCourseUsersResponse> & {
  courseUsers: ListCourseUsersResponse | undefined;
} => {
  const {
    take = 5,
    skip = 0,
    sort,
    filters,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [
      `${COURSES_QUERY_KEY}CourseUsers`,
      take,
      skip,
      JSON.stringify(sort),
      JSON.stringify(filters),
    ],
    () => client.courses.getCourseUsers(params, courseId, accountId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as any),
    },
  );

  return {
    courseUsers: data,
    ...(rest as UseQueryResult<ListCourseUsersResponse>),
  };
};
